import React, { useEffect, useState } from "react";
import { Transition } from "@headlessui/react";
import {
  useNotification,
  type Notification,
} from "../../context/NotificationContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faExclamationCircle,
  faInfoCircle,
  faExclamationTriangle,
  faSpinner,
  faBell,
  faTimes,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import { Button } from "@/components/ui/button/Button";

interface NotificationItemProps {
  notification: Notification;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
}) => {
  const { removeNotification } = useNotification();
  const [show, setShow] = useState(true);

  // Handle close notification
  const handleClose = () => {
    setShow(false);
    // Adding a slight delay to allow the transition to complete
    setTimeout(() => {
      removeNotification(notification.id);
    }, 300);
  };

  // Get the appropriate icon based on notification type
  const getIcon = () => {
    switch (notification.type) {
      case "success":
        return (
          <FontAwesomeIcon
            icon={faCheckCircle}
            className="text-green-400 dark:text-green-300 text-xl"
            aria-hidden="true"
          />
        );
      case "error":
        return (
          <FontAwesomeIcon
            icon={faExclamationCircle}
            className="text-red-400 dark:text-red-300 text-xl"
            aria-hidden="true"
          />
        );
      case "warning":
        return (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className="text-yellow-400 dark:text-yellow-300 text-xl"
            aria-hidden="true"
          />
        );
      case "info":
        return (
          <FontAwesomeIcon
            icon={faInfoCircle}
            className="text-indigo-400 dark:text-indigo-300 text-xl"
            aria-hidden="true"
          />
        );
      case "progress":
        return (
          <FontAwesomeIcon
            icon={faSpinner}
            className="text-indigo-400 dark:text-indigo-300 text-xl fa-spin"
            aria-hidden="true"
          />
        );
      default:
        return (
          <FontAwesomeIcon
            icon={faBell}
            className="text-gray-400 dark:text-gray-300 text-xl"
            aria-hidden="true"
          />
        );
    }
  };

  return (
    <Transition
      show={show}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white/90 shadow-xl shadow-black/10 dark:shadow-black/15  dark:bg-slate-900/80 backdrop-blur-sm ">
        <div className="p-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">{getIcon()}</div>
            <div className="ml-3 w-0 flex-1">
              <p className=" font-medium text-gray-900 dark:text-white">
                {notification.title}
              </p>
              {notification.message && (
                <p className="mt-1 whitespace-pre-line text-sm text-gray-500 dark:text-gray-400">
                  {notification.message}
                </p>
              )}

              {/* Progress bar for progress notifications */}
              {notification.type === "progress" &&
                typeof notification.progress === "number" && (
                  <div className="mt-2">
                    <div className="relative h-2 w-full rounded-full bg-gray-200 dark:bg-gray-700">
                      <div
                        className="absolute left-0 top-0 h-full rounded-full bg-indigo-500 transition-all duration-200"
                        style={{ width: `${notification.progress}%` }}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      {notification.progress}% Complete
                    </p>
                  </div>
                )}

              {/* Action button if provided */}
              {notification.action && (
                <div className="mt-3">
                  <Button
                    outline
                    size="sm"
                    onClick={() => {
                      notification.action?.onClick();
                      handleClose();
                    }}
                  >
                    {notification.action.label}
                  </Button>
                </div>
              )}
            </div>
            <div className="ml-4 flex flex-shrink-0">
              <button
                type="button"
                className="inline-flex rounded-md bg-transparent text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 dark:bg-transparent dark:text-gray-500 dark:hover:text-gray-400 dark:focus:ring-indigo-400"
                onClick={handleClose}
              >
                <span className="sr-only">Close</span>
                <FontAwesomeIcon
                  icon={faTimes}
                  className="text-lg"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};
